import {
  addYears,
  format as formatDate,
  getYear,
  isEqual,
  isFuture,
  isPast,
  isToday,
  parse,
  setYear,
  subYears,
} from "date-fns"
import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react"
import { DisabledModeT } from "shared-ts/hooks/useFilter/types/filter.types"

import { TextInput, TextInputProps } from "../TextInput"

export interface TextDateInputProps extends TextInputProps {
  date: Date | null
  onChangeDate: (d: Date | null) => void
  defaultRefDate: Date
  format?: string
  disabledMode?: DisabledModeT
  ref?: RefObject<HTMLInputElement | null>
}

export const TextDateInput = (props: TextDateInputProps) => {
  const {
    ref,
    date,
    onChangeDate,
    defaultRefDate,
    format: fmt = "MMM d, yyyy",
    onBlur: onBlurProp,
    disabledMode,
    ...otherProps
  } = props

  const useTextDateInputRes = useTextDateInput({
    ref,
    date,
    defaultRefDate,
    format: fmt,
    disabledMode,
    onChangeDate,
    onBlurProp,
  })

  return (
    <TextInput
      ref={useTextDateInputRes.inputRef}
      value={useTextDateInputRes.dateString}
      onChange={useTextDateInputRes.onChange}
      onBlur={useTextDateInputRes.onBlur}
      onKeyDown={useTextDateInputRes.onKeyDown}
      {...otherProps}
    />
  )
}

interface UseTextDateInputProps {
  ref?: RefObject<HTMLInputElement | null>
  date: Date | null
  defaultRefDate: Date
  format: string
  disabledMode?: DisabledModeT
  onChangeDate: (d: Date | null) => void
  onBlurProp?: FocusEventHandler<HTMLInputElement>
}

export function useTextDateInput({
  ref,
  date,
  defaultRefDate,
  format: fmt,
  disabledMode,
  onChangeDate,
  onBlurProp,
}: UseTextDateInputProps) {
  const internalRef = useRef<HTMLInputElement>(null)
  const inputRef = ref ?? internalRef

  const [dateString, setDateString] = useState<string>(
    date ? formatDate(date, fmt) : ""
  )
  const [stagedDate, setStagedDate] = useState<Date | null>(date)

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDateString(e.target.value)

    if (!e.target.value) {
      setStagedDate(null)
      return
    }

    let parsed = parseDate(e.target.value, date ?? defaultRefDate)

    if (!parsed) {
      setStagedDate(date)
      return
    }

    if (disabledMode === "past" && isPast(parsed) && !isToday(parsed)) {
      parsed = setYear(parsed, getYear(new Date()))
      if (isPast(parsed)) {
        parsed = addYears(parsed, 1)
      }
    }

    if (disabledMode === "future" && isFuture(parsed) && !isToday(parsed)) {
      parsed = setYear(parsed, getYear(new Date()))
      if (isFuture(parsed)) {
        parsed = subYears(parsed, 1)
      }
    }

    setStagedDate(parsed)
  }

  useEffect(() => {
    const formatted = date ? formatDate(date, fmt) : ""

    if (dateString === formatted) {
      return
    }

    setDateString(formatted)
  }, [date])

  useEffect(() => {
    if (!date && !stagedDate) {
      return
    }

    if (date && stagedDate && isEqual(date, stagedDate)) {
      return
    }

    setStagedDate(date)
  }, [date])

  const onBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setDateString(stagedDate ? formatDate(stagedDate, fmt) : "")
    if (
      (!!date && !stagedDate) ||
      (!date && !!stagedDate) ||
      (stagedDate && date && !isEqual(stagedDate, date))
    ) {
      onChangeDate(stagedDate)
    }

    if (onBlurProp) {
      onBlurProp(e)
    }
  }

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key !== "Enter") {
      return
    }

    if (!inputRef.current) {
      return
    }

    setDateString(stagedDate ? formatDate(stagedDate, fmt) : "")
    onChangeDate(stagedDate)
    inputRef.current.blur()
  }

  return {
    inputRef,
    dateString,
    onChange,
    onBlur,
    onKeyDown,
  }
}

export function parseDate(str: string, refDate: Date): Date | null {
  const formats = [
    "MMM d, yyyy", // Apr 3, 2023
    "MMM d yyyy", // Apr 3 2023
    "MMM do, yyyy", // Apr 3rd, 2023
    "MMM do yyyy", // Apr 3rd 2023
    "MMM dd, yyyy", // Apr 03, 2023
    "MMM dd yyyy", // Apr 03 2023
    "MMMM d, yyyy", // April 3, 2023
    "MMMM d yyyy", // April 3 2023
    "MMMM do, yyyy", // April 3rd, 2023
    "MMMM do yyyy", // April 3rd 2023
    "MMMM dd, yyyy", // April 03, 2023
    "MMMM dd yyyy", // April 03 2023
    "M/d/yy", // 4/3/23
    "M/d/yyyy", // 4/3/2023
    "MM/dd/yy", // 04/03/23
    "MM/dd/yyyy", // 04/03/2023
    "MMM d", // Apr 3
    "MMM do", // Apr 3rd
    "MMM dd", // Apr 03
    "MMMM d", // April 3
    "MMMM do", // April 3rd
    "MMMM dd", // April 03
    "M/d", // 4/3
    "MM/dd", // 04/03
  ]

  for (const f of formats) {
    const date = parse(str, f, refDate)

    if (!(date instanceof Date)) {
      continue
    }

    if (Number.isNaN(date.getTime())) {
      continue
    }

    return date
  }

  return null
}
